import { onUnmounted } from 'vue';

export type SessionSyncOptions = {
  /** localStorage key the auth store persists its state under (e.g. `pinia-auth`). */
  storageKey: string;
  /** Id of the user this tab is signed in as; null/undefined while signed out. */
  getUserId: () => string | null | undefined;
  /**
   * Called once when another tab ended or replaced this tab's session.
   * Defaults to a full page navigation to `/`.
   *
   * It navigates to `/`, not `/login`: by the time this fires another user may
   * already be signed in on this device, and a fresh load of `/login` while a
   * session exists signs that session out. `/` lands on the app as whoever is
   * signed in, or on the login page if nobody is.
   *
   * It should NOT clear the auth store: a persisted store writes every change
   * back to localStorage, which would overwrite the session the other tab just
   * stored. A full navigation discards this tab's in-memory tokens instead.
   */
  onSessionEnded?: () => void;
};

/** Pulls the signed-in user's id out of the persisted auth state, or null if it holds no session. */
function readStoredUserId(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const state = JSON.parse(raw) as { accessToken?: unknown; user?: { id?: unknown } | null };
    if (!state?.accessToken) return null;
    const id = state.user?.id;
    return typeof id === 'string' ? id : null;
  } catch {
    return null;
  }
}

/**
 * Keeps every tab of the app consistent with the single session held in
 * localStorage.
 *
 * The session is persisted to localStorage (shared by all tabs) but each tab
 * also keeps its own copy in memory. Without this, a tab left open by one user
 * keeps acting as them after another person signs them out and signs in on the
 * same device — and can even write the old user's tokens back to storage.
 *
 * Reacts to the `storage` event (fired only in the *other* tabs) when the
 * stored session is cleared, removed, or now belongs to a different user. A
 * change for the same user (e.g. another tab refreshed the tokens) is ignored.
 */
export function useSessionSync(options: SessionSyncOptions): void {
  const {
    storageKey,
    getUserId,
    onSessionEnded = () => window.location.assign('/'),
  } = options;

  let ended = false;

  function handleStorage(event: StorageEvent): void {
    if (ended) return;
    if (event.storageArea && event.storageArea !== window.localStorage) return;
    // key === null means localStorage.clear() ran in another tab.
    if (event.key !== null && event.key !== storageKey) return;

    const currentUserId = getUserId();
    // Signed out in this tab: nothing to protect.
    if (!currentUserId) return;

    const storedUserId = event.key === null ? null : readStoredUserId(event.newValue);
    // Same user (e.g. tokens refreshed in another tab): the session is still ours.
    if (storedUserId === currentUserId) return;

    ended = true;
    onSessionEnded();
  }

  window.addEventListener('storage', handleStorage);
  onUnmounted(() => {
    window.removeEventListener('storage', handleStorage);
  });
}
